/**
 * <bkg-gallery-overlays>
 *
 * Injects text cards (from product.metafields.bkg_.gallery_overlays, emitted as
 * JSON by snippets/bkg-gallery-overlays.liquid) into the product gallery slides,
 * and captions the matching thumbnails.
 *
 * No shopper-facing copy lives here. Entry copy comes from the metaobjects,
 * thumbnail labels from data-label-* attributes resolved from locale files.
 *
 * Slides are addressed by the --slide-N custom property Horizon writes into each
 * slideshow-slide's style attribute, not by DOM order, so a gallery that renders
 * extra nodes cannot shift the mapping.
 */

const SLIDE_INDEX = /--slide-(\d+)/;
const THUMB_INDEX = /\/select\/(\d+)/;
const MARK = 'data-bkg-overlay';

class BkgGalleryOverlays extends HTMLElement {
  /** @type {Array<Record<string, any>>} */
  #entries = [];
  /** @type {MutationObserver | null} */
  #observer = null;
  #frame = 0;

  connectedCallback() {
    this.gallery = this.closest('media-gallery');
    if (!this.gallery) return;

    try {
      const raw = this.querySelector('script[type="application/json"]')?.textContent || '[]';
      this.#entries = JSON.parse(raw);
    } catch (error) {
      console.warn('[bkg-gallery-overlays] invalid JSON payload', error);
      return;
    }

    this.#entries = this.#entries.filter((entry) => this.#passesBuyBoxGate(entry));
    this.#apply();

    // Horizon re-renders the product section on variant change. Re-apply when the
    // gallery's slides are replaced; #apply is idempotent, so our own writes do
    // not cause further work.
    this.#observer = new MutationObserver(() => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#apply());
    });
    this.#observer.observe(this.gallery, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  /**
   * Subscription gate, JS half. Liquid already dropped the slide when no visible
   * variant carries a selling plan. The buy box can still hide every subscription
   * option (offer_mode, pack picker tier guard), so read what it rendered.
   */
  #passesBuyBoxGate(entry) {
    if (entry.type !== 'subscription') return true;
    const section = this.closest('.shopify-section') || document;
    const signals = [...section.querySelectorAll('[data-has-plans], [data-sub-available]')];
    if (signals.length === 0) return true;
    return signals.some((el) => (el.dataset.hasPlans ?? el.dataset.subAvailable) === 'true');
  }

  #apply() {
    if (!this.gallery) return;
    const slides = this.#slidesByIndex();
    const thumbs = this.#thumbsByIndex();

    for (const entry of this.#entries) {
      const slide = slides.get(entry.slide);
      if (slide && !slide.querySelector(`[${MARK}]`)) {
        slide.append(this.#card(entry));
      }

      const thumb = thumbs.get(entry.slide);
      const label = this.dataset[`label${entry.type.charAt(0).toUpperCase()}${entry.type.slice(1)}`];
      if (thumb && label && !thumb.querySelector(`[${MARK}]`)) {
        const caption = document.createElement('span');
        caption.className = 'bkg-thumb-caption';
        caption.setAttribute(MARK, '');
        caption.setAttribute('aria-hidden', 'true');
        caption.textContent = label;
        thumb.append(caption);
      }
    }

    this.gallery.toggleAttribute('data-bkg-overlays', this.#entries.length > 0);
  }

  #slidesByIndex() {
    const map = new Map();
    for (const slide of this.gallery.querySelectorAll('slideshow-slide')) {
      if (slide.closest('dialog')) continue;
      const match = (slide.getAttribute('style') || '').match(SLIDE_INDEX);
      if (match) map.set(Number(match[1]), slide);
    }
    return map;
  }

  #thumbsByIndex() {
    const map = new Map();
    for (const thumb of this.gallery.querySelectorAll('.slideshow-controls__thumbnail')) {
      const match = (thumb.getAttribute('on:click') || '').match(THUMB_INDEX);
      if (match) map.set(Number(match[1]), thumb);
    }
    return map;
  }

  #card(entry) {
    const card = document.createElement('div');
    card.className = `bkg-overlay bkg-overlay--${entry.type}`;
    card.setAttribute(MARK, entry.type);

    const add = (tag, className, text) => {
      if (!text) return null;
      const el = document.createElement(tag);
      el.className = className;
      el.textContent = text;
      card.append(el);
      return el;
    };

    add('p', 'bkg-overlay__eyebrow', entry.eyebrow);
    add('p', 'bkg-overlay__heading', entry.heading);
    add('p', 'bkg-overlay__subheading', entry.subheading);

    if (Array.isArray(entry.chips) && entry.chips.length) {
      const list = document.createElement('ul');
      list.className = 'bkg-overlay__list';
      for (const chip of entry.chips) {
        if (!chip) continue;
        const item = document.createElement('li');
        item.textContent = chip;
        list.append(item);
      }
      card.append(list);
    }

    if (entry.quote) {
      const quote = document.createElement('blockquote');
      quote.className = 'bkg-overlay__quote';
      const text = document.createElement('p');
      text.textContent = entry.quote;
      quote.append(text);
      if (entry.quoteAuthor) {
        const author = document.createElement('cite');
        author.textContent = entry.quoteAuthor;
        quote.append(author);
      }
      card.append(quote);
    }

    if (entry.linkLabel && entry.linkHref) {
      const link = document.createElement('a');
      link.className = 'bkg-overlay__link';
      link.href = entry.linkHref;
      link.textContent = entry.linkLabel;
      link.addEventListener('click', (event) => this.#followLink(event, entry.linkHref));
      card.append(link);
    }

    // Slides open the zoom dialog on click. A click on the card's text is not a
    // request to zoom, and a link inside it must never do both.
    card.addEventListener('click', (event) => event.stopPropagation());

    return card;
  }

  /**
   * In-page anchors name a section by its schema id ("#ingredients"). Shopify
   * section wrappers carry ids like "shopify-section-template--123__ingredients_ab12",
   * so fall back to a suffix match when no exact id exists.
   */
  #followLink(event, href) {
    if (!href.startsWith('#')) return;
    const slug = href.slice(1);
    const target =
      document.getElementById(slug) ||
      document.querySelector(`.shopify-section[id*="__${CSS.escape(slug)}"]`) ||
      document.querySelector(`.${CSS.escape(slug)}-section`);
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

if (!customElements.get('bkg-gallery-overlays')) {
  customElements.define('bkg-gallery-overlays', BkgGalleryOverlays);
}
